"use client"

import { useEffect, useMemo, useState, type ReactNode } from "react"
import Link from "next/link"
import Image from "next/image"
import {
  ArrowRight,
  BarChart,
  Book,
  BookOpen,
  Calendar,
  Check,
  Clock,
  Crown,
  Eye,
  Heart,
  Layers,
  Leaf,
  Lock,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react"
import posthog from "posthog-js"

import { Footer } from "@/components/footer"
import { PromotionBadge } from "@/components/promotion-badge"
import { BrowserFrame } from "@/components/browser-frame"
import { HeroMotion } from "@/components/hero-motion"
import { Reveal } from "@/components/scroll-reveal"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useSupabase } from "@/lib/supabase/context"
import { getPlanDetails, getProductByPlanName, getYearlySavingsWithPromotion } from "@/lib/products"

const ctaProps = {
  "data-fast-goal": "signup_cta_click",
}

/** Small inline citation that attaches real, published research to a claim. */
function ResearchNote({
  stat,
  children,
  source,
  href,
}: {
  stat: string
  children: ReactNode
  source: string
  href: string
}) {
  return (
    <div className="inline-flex items-start gap-3 rounded-lg border border-primary/15 bg-primary/5 px-4 py-3 text-left text-sm text-muted-foreground">
      <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <span>
        <strong className="text-foreground">{stat}</strong> {children}{" "}
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          — {source}
        </a>
      </span>
    </div>
  )
}

/** Section eyebrow — small caps label above a heading. */
function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{children}</p>
}

/**
 * ScreenStack — the hero's load-bearing visual: the week you plan, next to the
 * year it rolls up into. Two shots, not a gallery — landing-page research is
 * consistent that the above-the-fold screenshot carries the most conversion
 * weight, and extra frames here only dilute it. The daily page gets its own
 * moment in the tour below, so it is deliberately not repeated here.
 *
 * The drift animation and the tilt live on separate elements on purpose — both
 * write to `transform`, so nesting keeps the keyframes from cancelling the tilt.
 */
function ScreenStack() {
  return (
    <div className="relative mx-auto w-full max-w-5xl">
      <div className="grid gap-6 md:grid-cols-12 md:items-center">
        <div className="md:col-span-7 md:pt-6">
          <div className="float-soft">
            <div className="transition-transform duration-500 ease-out md:-rotate-[1.2deg] md:hover:rotate-0">
              <BrowserFrame
                src="/blog-images/this-week-view.jpg"
                alt="Planami weekly planner view with week goals, notes and daily columns"
                label="planami.app/planner"
                priority
                sizes="(max-width: 768px) 100vw, 58vw"
              />
            </div>
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="float-soft-delayed">
            <div className="transition-transform duration-500 ease-out md:rotate-[1.6deg] md:hover:rotate-0">
              <BrowserFrame
                src="/blog-images/year-view.jpg"
                alt="Planami year at a glance view"
                label="planami.app/planner"
                sizes="(max-width: 768px) 100vw, 40vw"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CTAButton({
  user,
  location,
  variant = "default",
}: {
  user: any
  location: string
  variant?: "default" | "secondary" | "outline"
}) {
  if (user) {
    return (
      <Link href="/planner">
        <Button size="lg" variant={variant} className="w-full gap-2 sm:w-auto">
          Go to your planner <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    )
  }

  return (
    <Link
      href="/auth/sign-up"
      className="w-full sm:w-auto"
      {...ctaProps}
      data-fast-goal-location={location}
      onClick={() => posthog.capture("pricing_cta_clicked", { location })}
    >
      <Button size="lg" variant={variant} className="w-full gap-2 sm:w-auto">
        Start your 7-day free trial <ArrowRight className="h-4 w-4" />
      </Button>
    </Link>
  )
}

/**
 * Three pillars, one screenshot each — plan, pursue, reflect.
 *
 * Deliberately not one row per feature: landing-page research is consistent
 * that conversion falls away past ~6-8 distinct screenshots, and that repeating
 * the same wide product shot trains visitors to skim. Everything else lives in
 * the single module-list screenshot below.
 */
const MODULE_TOUR = [
  {
    icon: Calendar,
    title: "Plan the day you'll actually live",
    src: "/blog-images/daily-view.jpg",
    label: "planami.app/planner",
    alt: "Daily planner view with schedule, focus tasks and gratitude",
    points: [
      "Schedule, top-3 focus and optional tasks on one page",
      "Switch between year, month, week and daily layouts",
      "Gratitude and wellness right where you plan",
    ],
    note: "Never guess what to do next, just follow the page.",
  },
  {
    icon: Target,
    title: "Big goals that reach your calendar",
    src: "/blog-images/big-goals.jpg",
    label: "planami.app/planner/life-goals",
    alt: "Life goals module",
    points: ["Break big dreams into small steps", "Track long-range goals across years", "Watch progress fill in visually"],
    research: {
      stat: "76% vs 43%.",
      source: "Matthews, Dominican University",
      href: "https://scholar.dominican.edu/psychology-faculty-conference-presentations/3/",
      body: "People who write down their goals and track progress reach far more of them.",
    },
  },
  {
    icon: Heart,
    title: "Reflection that makes progress visible",
    src: "/blog-images/wellness.jpg",
    label: "planami.app/planner/wellness",
    alt: "Wellness and reflection features",
    points: ["Mood, habits and energy tracking", "Gratitude notes in two lines a day", "Weekly, monthly and yearly reviews"],
    research: {
      stat: "More optimism, fewer ailments.",
      source: "Emmons & McCullough, 2003",
      href: "https://en.wikipedia.org/wiki/Robert_A._Emmons",
      body: "People who regularly write down what they're grateful for report higher wellbeing.",
    },
  },
]

/** Named in text beside the module-list screenshot, rather than as a thumbnail
 *  grid — at a third of the column width the screenshots were unreadable. */
const MORE_MODULES = [
  "Moodboard",
  "Travel planner",
  "Date nights",
  "Financial goals",
  "Books & movies",
  "Wishes",
  "Monthly review",
  "Yearly reflection",
]

const RESEARCH = [
  {
    icon: Target,
    figure: "76% vs 43%",
    title: "Writing goals down works",
    body: "In a study of 267 people, those who wrote their goals and sent weekly progress updates reached or were halfway to them 76% of the time — versus 43% for those who only thought about them.",
    source: "Matthews, Dominican University",
    href: "https://scholar.dominican.edu/psychology-faculty-conference-presentations/3/",
  },
  {
    icon: RefreshCw,
    figure: "+22.8%",
    title: "Reflection improves performance",
    body: "People who spent a few minutes reflecting on what they'd done scored 22.8% higher than those who simply kept working — the science behind our weekly, monthly and yearly reviews.",
    source: "Di Stefano, Gino & Pisano, Harvard",
    href: "https://www.hbs.edu/faculty/Pages/item.aspx?num=63487",
  },
  {
    icon: Eye,
    figure: "138 studies",
    title: "Tracking drives results",
    body: "A meta-analysis of 138 studies found that prompting people to monitor their progress reliably increased the chance they'd reach their goal — and the effect was stronger when progress was physically recorded.",
    source: "Harkin et al., 2016",
    href: "https://en.wikipedia.org/wiki/Goal_pursuit",
  },
  {
    icon: Calendar,
    figure: "94 studies",
    title: "Concrete plans get done",
    body: "Deciding when and where you'll act — not just what you want — produces a medium-to-large increase in follow-through across 94 studies. That's why Planami turns goals into scheduled weeks and days.",
    source: "Gollwitzer & Sheeran, 2006",
    href: "https://en.wikipedia.org/wiki/Implementation_intention",
  },
  {
    icon: Heart,
    figure: "More optimism",
    title: "Gratitude lifts wellbeing",
    body: "People who regularly wrote down what they were grateful for were more optimistic, exercised more, and reported fewer physical complaints — the everyday practice built into your daily page.",
    source: "Emmons & McCullough, 2003",
    href: "https://en.wikipedia.org/wiki/Robert_A._Emmons",
  },
]

const REPLACED_TOOLS = [
  { icon: Calendar, label: "Paper planner", cost: "~$30/yr" },
  { icon: BarChart, label: "Habit tracker", cost: "~$50/yr" },
  { icon: Book, label: "Journal app", cost: "~$60/yr" },
  { icon: Target, label: "Goal coaching", cost: "$100+/session" },
]

export default function HomePage() {
  const [user, setUser] = useState<any>(null)
  const [pricingInterval, setPricingInterval] = useState<"monthly" | "yearly">("yearly")
  const supabase = useSupabase()

  const creatorPlan = useMemo(() => getPlanDetails("creator", pricingInterval), [pricingInterval])
  const dreamerPlan = useMemo(() => getPlanDetails("dreamer", pricingInterval), [pricingInterval])
  const creatorSavings = useMemo(() => getYearlySavingsWithPromotion("creator"), [])
  const dreamerSavings = useMemo(() => getYearlySavingsWithPromotion("dreamer"), [])

  useEffect(() => {
    const loadUser = async () => {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser()

        if (error) {
          const message = error.message || ""
          if (
            message.includes("Invalid Refresh Token") ||
            message.includes("Refresh Token Not Found") ||
            message.includes("refresh token") ||
            message.includes("Auth session missing") ||
            message.includes("AuthSessionMissingError")
          ) {
            setUser(null)
            return
          }
        }

        setUser(user || null)
      } catch {
        setUser(null)
      }
    }

    loadUser()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string, session: any) => {
      if (event === "SIGNED_OUT" || !session) {
        setUser(null)
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        loadUser()
      }
    })

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The value anchor compares the $300+ tool shelf against the Dreamer yearly
  // plan. It shows the regular list price as the anchor and, while a promo is
  // running, strikes it through and leads with the price you actually pay — so
  // the number here always matches the pricing card below.
  const dreamerYearly = useMemo(() => getPlanDetails("dreamer", "yearly"), [])
  const dreamerListMonthly = ((getProductByPlanName("dreamer", "yearly")?.priceInCents || 9000) / 100 / 12).toFixed(2)
  const dreamerSaleMonthly = dreamerYearly?.promotion
    ? (dreamerYearly.priceInCents / 100 / 12).toFixed(2)
    : null

  const dreamerMonthly = ((dreamerPlan?.priceInCents || 9000) / 100 / (pricingInterval === "yearly" ? 12 : 1)).toFixed(2)

  return (
    <div className="min-h-screen bg-background">
      {/* ---------------------------------------------------------------- Hero */}
      <section className="paper-texture overflow-hidden border-b border-border/60">
        <div className="container mx-auto max-w-7xl px-4 pb-14 pt-8 md:pb-20 md:pt-14">
          <div className="grid items-center gap-10 md:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-card/80 px-4 py-2 text-sm text-muted-foreground shadow-sm">
                <Leaf className="h-4 w-4 text-primary" />
                Calm planning for ambitious lives
              </div>
              <h1 className="text-balance text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                The planner that turns your life into <span className="ink-highlight">clear path</span>.
              </h1>
              <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground md:text-xl">
                Planami brings your goals, days, reflections, habits, memories, and private plans into one paper-like
                space and makes progress visible.
              </p>

              <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                <CTAButton user={user} location="hero" />
                <Link
                  href="/yearly-reflect"
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium text-primary hover:underline sm:justify-start"
                >
                  <RefreshCw className="h-4 w-4 shrink-0" />
                  Try the free yearly reflection
                </Link>
              </div>

              {!user && (
                <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  {["Full Dreamer trial", "No card required", "Cancel anytime"].map((item) => (
                    <li key={item} className="flex items-center gap-1.5">
                      <Check className="h-4 w-4 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="relative h-[420px] md:h-[400px] lg:h-[460px]">
              <HeroMotion />
            </div>
          </div>

          <div className="mt-14 md:mt-20">
            <Reveal>
              <ScreenStack />
            </Reveal>
            <p className="mt-8 text-center text-sm italic text-muted-foreground">
              Real pages from the planner (here week and year), all connected with each other.
            </p>
          </div>
        </div>
      </section>

      {/* Proof strip — one line of evidence between the hero and the tour, so the
          claim above lands before the product walkthrough starts. */}
      <section className="border-b border-border/60 bg-muted/25 py-8 md:py-10">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <Reveal>
            <ResearchNote
              stat="Tracking makes the difference."
              source="Harkin et al., 2016 (138 studies)"
              href="https://en.wikipedia.org/wiki/Goal_pursuit"
            >
              When people monitor their progress, they&apos;re significantly more likely to reach their goals.
            </ResearchNote>
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------------- Guided tour */}
      <section id="features" className="scroll-mt-24 paper-texture py-16 md:py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <Reveal className="mx-auto max-w-3xl text-center">
            <Eyebrow>What you get</Eyebrow>
            <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-5xl">
              Everything you plan, in one place that feels like paper
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              You don't need to assemble a productivity stack. A planner is already set up when you open it.
            </p>
          </Reveal>

          <div className="mt-14 space-y-16 md:space-y-24">
            {MODULE_TOUR.map((item, index) => {
              const imageFirst = index % 2 === 1
              return (
                <Reveal
                  key={item.title}
                  direction={imageFirst ? "right" : "left"}
                  className={`flex flex-col items-center gap-8 md:gap-12 ${
                    imageFirst ? "md:flex-row-reverse" : "md:flex-row"
                  }`}
                >
                  <div className="flex-1">
                    <h3 className="flex items-center gap-2.5 text-2xl font-semibold leading-tight text-foreground">
                      <item.icon className="h-6 w-6 shrink-0 text-primary" />
                      {item.title}
                    </h3>
                    <ul className="mt-5 space-y-2.5 text-muted-foreground">
                      {item.points.map((point) => (
                        <li key={point} className="flex items-start gap-2.5">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                    {item.note && <p className="mt-5 text-sm font-medium italic text-primary">{item.note}</p>}
                    {item.research && (
                      <div className="mt-6">
                        <ResearchNote
                          stat={item.research.stat}
                          source={item.research.source}
                          href={item.research.href}
                        >
                          {item.research.body}
                        </ResearchNote>
                      </div>
                    )}
                  </div>

                  <BrowserFrame
                    className="w-full flex-1"
                    src={item.src}
                    alt={item.alt}
                    label={item.label}
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </Reveal>
              )
            })}
          </div>

          {/* "Plenty more" proved with the module list itself — one readable
              screenshot that shows every planner at once, beside the names. */}
          <Reveal
            direction="right"
            className="mt-20 flex flex-col items-center gap-8 md:mt-28 md:flex-row-reverse md:gap-12"
          >
            <div className="flex-1">
              <h3 className="flex items-center gap-2.5 text-2xl font-semibold leading-tight text-foreground">
                <Layers className="h-6 w-6 shrink-0 text-primary" />
                And the rest of your life gets a home too
              </h3>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Every planner lives behind one calm menu — no clutter, no pressure, no notes scattered across five apps.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {MORE_MODULES.map((name) => (
                  <li
                    key={name}
                    className="rounded-full border border-primary/20 bg-primary/5 px-3.5 py-1.5 text-sm text-foreground"
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </div>

            <BrowserFrame
              className="w-full flex-1"
              src="/blog-images/side-planners-now.jpg"
              alt="The Planami module list: calendar, big goals, books and movies, wishes, moodboard, travel, date nights and financial goals"
              label="planami.app/planner/modules"
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </Reveal>

          {/* Mid-page CTA — placed right after the tour, while the pages are
              still on screen and wanting one is the natural next thought. Kept
              as a soft paper card so it never competes with the closing band. */}
          <Reveal className="mx-auto mt-20 max-w-3xl md:mt-28">
            <div className="paper-sheet rounded-lg border-2 border-primary/45 p-8 text-center md:p-10">
              <h3 className="text-balance text-2xl font-semibold leading-tight md:text-3xl">
                These pages are waiting for your writing
              </h3>
              <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
                Open the planner and start with today. Nothing to set up, nothing to configure — your first page is
                already there.
              </p>
              <div className="mt-7 flex justify-center">
                <CTAButton user={user} location="mid_page" />
              </div>
              {!user && (
                <p className="mt-4 text-sm text-muted-foreground">
                  Full Dreamer trial for 7 days · No card required · Cancel anytime
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------- Cost of doing nothing */}
      <section className="bg-muted/25 py-16 md:py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <Reveal className="mx-auto max-w-3xl text-center">
            <Eyebrow>Why bother</Eyebrow>
            <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-5xl">
              &ldquo;Why would I spend time on this?&rdquo;
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Because the alternative is slower. Doing random things in random order, without a clear picture will not
              take you where you want — it just takes far longer to get nowhere.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal direction="left" className="paper-sheet rounded-lg border border-border p-6 md:p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted">
                  <RefreshCw className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold">Without a plan</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                {[
                  "Busy days that don't add up to anything",
                  "Effort spent on the wrong things",
                  "Restarting the same goals every year",
                  "100x the time, a fraction of the progress",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <ThumbsDown className="mt-0.5 h-5 w-5 shrink-0 opacity-60" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal direction="right" className="paper-sheet rounded-lg border-2 border-primary/45 p-6 md:p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-primary">With a clear picture</h3>
              </div>
              <ul className="space-y-3 text-foreground">
                {[
                  "Every week points at something that matters",
                  "The right actions, in the right order",
                  "Goals that actually get finished",
                  "The shortest path between you and what you want",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <ThumbsUp className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <Reveal className="mx-auto mt-12 max-w-2xl text-center">
            <p className="text-2xl font-semibold leading-snug text-foreground md:text-3xl">
              Five minutes of clarity beats hours of wandering
            </p>
            <p className="mt-3 text-lg leading-relaxed text-muted-foreground">
              A structured plan makes the clear picture possible, so your time finally compounds toward the life you
              actually want.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ----------------------------------------------------------- Time to value */}
      <section className="paper-texture py-16 md:py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <Reveal className="text-center">
            <Eyebrow>What you&apos;ll notice</Eyebrow>
            <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-4xl">
              A small ritual giving big clarity, starting this week
            </h2>
          </Reveal>

          <div className="relative mt-12">
            <div className="relative grid gap-6 md:grid-cols-3">
              {[
                ["Day 1", "Mental relief", "Everything important finally has a place."],
                ["Week 1", "Clearer focus", "You know what matters and what can wait."],
                ["Month 1", "Visible confidence", "You can point to progress, not just effort."],
              ].map(([time, title, copy], index) => (
                <Reveal key={time} direction="up" delay={index * 110}>
                  <div className="paper-sheet h-full rounded-lg border border-border p-6 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-primary/30 bg-card text-xs font-semibold text-primary">
                      {time}
                    </div>
                    <h3 className="text-xl font-semibold">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-12 grid gap-4 sm:grid-cols-3">
            {[
              [Clock, "5 minutes a day"],
              [Heart, "No discipline required"],
              [Sparkles, "Works with real life"],
            ].map(([Icon, label]) => (
              <div
                key={label as string}
                className="flex items-center justify-center gap-2.5 rounded-lg border border-border bg-card/70 px-4 py-3 text-sm font-medium text-muted-foreground"
              >
                <Icon className="h-4 w-4 text-primary" />
                {label as string}
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------------------ Proof bank */}
      <section id="research" className="scroll-mt-24 bg-muted/25 py-16 md:py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <Reveal className="mx-auto max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm text-primary">
              <BookOpen className="h-4 w-4" />
              The research behind Planami
            </div>
            <h2 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">
              Decades of science behind it
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Writing goals down, planning your week, daily gratitude, regular reviews — every part of Planami is built
              on published research in psychology and behavioral science.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {RESEARCH.map((entry, index) => (
              <Reveal key={entry.title} direction="up" delay={(index % 3) * 90}>
                <div className="paper-sheet flex h-full flex-col rounded-lg border border-border p-6">
                  <entry.icon className="mb-4 h-7 w-7 text-primary" />
                  <div className="text-3xl font-semibold text-primary">{entry.figure}</div>
                  <h3 className="mt-2 font-semibold text-foreground">{entry.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{entry.body}</p>
                  <a
                    href={entry.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    {entry.source} <ArrowRight className="h-3 w-3" />
                  </a>
                </div>
              </Reveal>
            ))}

            <Reveal direction="up" delay={180}>
              <div className="flex h-full flex-col justify-center rounded-lg border border-primary bg-primary/5 p-6">
                <Sparkles className="mb-4 h-7 w-7 text-primary" />
                <h3 className="font-semibold text-foreground">Planami makes it effortless</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  You don&apos;t have to assemble these habits yourself. Planami puts goal-setting, planning, gratitude
                  and reflection in one calm place — so the science just happens in five minutes a day.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- Value anchor + pricing */}
      <section id="pricing" className="scroll-mt-24 paper-texture py-16 md:py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-5xl">
              Start with the full experience
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Try Dreamer free for 7 days. If it does not make your life feel clearer, you have lost nothing.
            </p>
          </Reveal>

          {/* Value stack — what Planami replaces */}
          <Reveal className="mx-auto mt-12 max-w-3xl">
            <div className="paper-sheet rounded-lg border border-border p-6 md:p-8">
              <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Planami replaces a whole shelf of tools
              </p>
              <div className="mb-6 grid grid-cols-2 gap-5 md:grid-cols-4">
                {REPLACED_TOOLS.map((tool) => (
                  <div key={tool.label} className="text-center">
                    <tool.icon className="mx-auto mb-2 h-6 w-6 text-primary" />
                    <p className="text-sm font-medium text-foreground">{tool.label}</p>
                    <p className="text-xs text-muted-foreground">{tool.cost}</p>
                  </div>
                ))}
              </div>
              <div className="perforated-rule" />
              <p className="mt-6 text-center text-lg leading-relaxed text-foreground md:text-xl">
                Bought separately, that&apos;s <span className="font-semibold">$300+ a year</span>.
                <br className="hidden md:block" /> Planami is all of it for{" "}
                {dreamerSaleMonthly ? (
                  <>
                    <span className="text-muted-foreground line-through">${dreamerListMonthly}</span>{" "}
                    <span className="font-semibold text-primary">${dreamerSaleMonthly}/month</span> during the sale —
                    less than one coffee.
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-primary">${dreamerListMonthly}/month</span> — close to one
                    coffee.
                  </>
                )}
              </p>
            </div>
          </Reveal>

          <div className="mt-10 flex justify-center">
            <Tabs
              value={pricingInterval}
              onValueChange={(value) => {
                setPricingInterval(value as "monthly" | "yearly")
                posthog.capture("pricing_interval_toggled", { interval: value })
              }}
              className="w-auto"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="yearly">
                  Yearly <span className="ml-2 text-xs font-semibold text-primary">Save {dreamerSavings}%</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-[1.1fr_0.9fr]">
            <Card className="relative flex flex-col rounded-lg border-2 border-primary shadow-xl shadow-primary/10">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-sm font-medium text-primary-foreground">
                Best first experience
              </div>
              <CardHeader className="pt-8">
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Crown className="h-5 w-5 text-primary" />
                  {dreamerPlan?.name || "Dreamer"}
                </CardTitle>
                <CardDescription>Every planner module, every review, every calm planning surface.</CardDescription>
                {dreamerPlan?.promotion && (
                  <PromotionBadge
                    badgeText={dreamerPlan.promotion.badgeText}
                    showCountdown={dreamerPlan.promotion.showCountdown}
                    endDate={dreamerPlan.promotion.endDate}
                    variant="default"
                  />
                )}
                <div className="mt-4">
                  {pricingInterval === "yearly" ? (
                    <>
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-4xl font-bold">{dreamerPlan?.price || "$90/year"}</span>
                        <span className="rounded bg-primary/10 px-2 py-0.5 text-sm font-semibold text-primary">
                          Save {dreamerSavings}%
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">${dreamerMonthly}/month billed annually</p>
                    </>
                  ) : (
                    <div>
                      <span className="text-4xl font-bold">{dreamerPlan?.price || "$9"}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="grid gap-3 sm:grid-cols-2">
                  {(dreamerPlan?.features || []).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {user ? (
                  <Link href="/profile" className="w-full">
                    <Button className="w-full">Manage Subscription</Button>
                  </Link>
                ) : (
                  <Link href="/auth/sign-up" className="w-full" {...ctaProps} data-fast-goal-location="pricing_dreamer">
                    <Button className="w-full gap-2">
                      Start planning free <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                )}
              </CardFooter>
            </Card>

            <Card className="flex flex-col rounded-lg border-2">
              <CardHeader>
                <CardTitle className="text-2xl">{creatorPlan?.name || "Creator"}</CardTitle>
                <CardDescription>A focused planner for calendar, lists, stickers, and core planning.</CardDescription>
                {creatorPlan?.promotion && (
                  <PromotionBadge
                    badgeText={creatorPlan.promotion.badgeText}
                    showCountdown={creatorPlan.promotion.showCountdown}
                    endDate={creatorPlan.promotion.endDate}
                    variant="secondary"
                  />
                )}
                <div className="mt-4">
                  {pricingInterval === "yearly" ? (
                    <>
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-4xl font-bold">{creatorPlan?.price || "$50/year"}</span>
                        <span className="rounded bg-primary/10 px-2 py-0.5 text-sm font-semibold text-primary">
                          Save {creatorSavings}%
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        ${((creatorPlan?.priceInCents || 5000) / 100 / 12).toFixed(2)}/month billed annually
                      </p>
                    </>
                  ) : (
                    <div>
                      <span className="text-4xl font-bold">{creatorPlan?.price || "$5"}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-3">
                  {(creatorPlan?.features || []).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {user ? (
                  <Link href="/profile" className="w-full">
                    <Button className="w-full bg-transparent" variant="outline">
                      Manage Subscription
                    </Button>
                  </Link>
                ) : (
                  <Link href="/auth/sign-up" className="w-full" {...ctaProps} data-fast-goal-location="pricing_creator">
                    <Button className="w-full bg-transparent" variant="outline">
                      Start now
                    </Button>
                  </Link>
                )}
              </CardFooter>
            </Card>
          </div>

          {/* Risk reversal */}
          <Reveal className="mx-auto mt-12 max-w-3xl">
            <div className="paper-sheet washi-tape relative rounded-lg border-2 border-primary/45 p-6 text-center md:p-8">
              <ShieldCheck className="mx-auto mb-4 h-8 w-8 text-primary" />
              <h3 className="text-2xl font-semibold leading-tight">You carry none of the risk</h3>
              <p className="mx-auto mt-3 max-w-xl leading-relaxed text-muted-foreground">
                Seven days of the full Dreamer plan without a card. If you subscribe and it still is not making your
                life feel clearer, tell us within 30 days and we return your money. Your planner data stays yours either
                way.
              </p>
              <div className="mt-6 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
                {[
                  [Clock, "7 days free, no card"],
                  [RefreshCw, "30-day money-back promise"],
                  [Lock, "Your data is never sold"],
                ].map(([Icon, label]) => (
                  <div
                    key={label as string}
                    className="flex items-center justify-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2.5"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-primary" />
                    {label as string}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* -------------------------------------------------------- Founder + FAQ */}
      <section className="bg-muted/25 py-16 md:py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <Reveal className="text-center">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold md:text-4xl">Everything else you might be wondering</h2>
          </Reveal>

          <div className="mt-10 grid items-start gap-8 md:grid-cols-[0.85fr_1.15fr]">
            <Reveal direction="left">
              <div className="paper-sheet rounded-lg border border-border p-6">
                <div className="flex gap-4">
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full">
                    <Image
                      src="/images/me.png"
                      alt="Maryna, creator of Planami"
                      fill
                      className="object-cover object-top"
                      sizes="64px"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Hey, I&apos;m Maryna.</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      I built Planami because I wanted a planner that helped me value my days, not judge them. The
                      purpose is to extend what paper planners can do — one structured place for your schedule, your
                      main tasks, the optional ones, and honest retrospectives. Regular reviews changed my own life, and
                      I hope they help yours too.
                    </p>
                    <a
                      href="mailto:support@planami.app"
                      className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    >
                      Ask me anything <ArrowRight className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal direction="right">
              <Accordion type="single" collapsible className="paper-sheet w-full rounded-lg border border-border px-4">
                <AccordionItem value="trial">
                  <AccordionTrigger>Do I need a credit card for the 7-day trial?</AccordionTrigger>
                  <AccordionContent>
                    No. The trial is completely free and requires no card. You get full access to the Dreamer plan for
                    the whole seven days.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="payment">
                  <AccordionTrigger>When do I need to pay, and what if I don&apos;t like it?</AccordionTrigger>
                  <AccordionContent>
                    Only after the trial ends, and only if you choose a plan. If you subscribe and see no value within
                    30 days, tell us and we return your money.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="cancel">
                  <AccordionTrigger>Can I cancel anytime?</AccordionTrigger>
                  <AccordionContent>
                    Yes, from your profile settings. You keep access until the end of the current billing period, and
                    you can resubscribe whenever you are ready.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="data">
                  <AccordionTrigger>What happens to my data if I cancel?</AccordionTrigger>
                  <AccordionContent>
                    Your planner remains yours. After cancelling you have a grace period to view everything, and your
                    account stays accessible afterwards — you just need an active plan to keep editing. We never delete
                    your data without your explicit request.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="difference">
                  <AccordionTrigger>What&apos;s the difference between Creator and Dreamer?</AccordionTrigger>
                  <AccordionContent>
                    Creator includes all calendar views, unlimited stickers, three planner modules (Calendar, Books &amp;
                    Movies, Wishes) and basic templates. Dreamer adds every premium module — life goals, moodboard,
                    travel, date nights, financial — plus premium retrospective and wellness templates.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="privacy">
                  <AccordionTrigger>How is my planner protected?</AccordionTrigger>
                  <AccordionContent>
                    Planami uses authenticated accounts, encrypted transport, row-level database security, and a
                    privacy-first product philosophy. We do not sell your planner data to anyone.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="fit">
                  <AccordionTrigger>Who is Planami best for?</AccordionTrigger>
                  <AccordionContent>
                    People who love paper planners, reflective planning, goals and journaling — but want the
                    flexibility, memory and privacy of a web app. Especially if you are tired of tools that push you.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="mobile">
                  <AccordionTrigger>Does it work on my phone?</AccordionTrigger>
                  <AccordionContent>
                    Yes — Planami runs in any modern browser and installs to your home screen. Dense planning weeks and months feel
                    best on a larger screen, but it works well on mobile too.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- Closing CTA */}
      <section className="paper-texture py-16 md:py-24">
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <Reveal>
            <h2 className="text-balance text-3xl font-semibold leading-tight md:text-4xl">
              Big dreams are built from ordinary days
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Your goals don&apos;t live in the future. They live in how you spend this week. <br/> Planami helps you see that.
            </p>
            <p className="mt-8 text-xl font-semibold leading-snug text-foreground md:text-2xl">
              In 12 months you&apos;ll look back and see exactly who you became.
              <span className="text-primary"> Feel proud you started today.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {!user && (
        <section className="bg-primary py-16 text-primary-foreground md:py-20">
          <div className="container mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-balance text-3xl font-semibold md:text-5xl">Your life is already full. Make it visible.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed opacity-90">
              Start with the full Planami experience today. <br/>No card, no pressure, just a calmer way to plan.
            </p>
            <div className="mt-7 flex justify-center">
              <CTAButton user={user} location="final_cta" variant="secondary" />
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 text-sm opacity-80">
              <Crown className="h-4 w-4" />
              <span>7-day full Dreamer trial • No card required • 30-day money-back promise</span>
            </div>
          </div>
        </section>
      )}

      <Footer user={user} />
    </div>
  )
}
